import React, { FC, useState, Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  IconButton,
  Avatar,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
  makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Assessment as AnalyticsIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  SupervisorAccount as SuperAdminIcon,
  TrendingUp as TrendingUpIcon,
  Email as EmailIcon
} from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { TokenManagerStore } from 'core/auth/TokenManager';
import CURRENT_USER from 'core/graphql/queries/currentUser';
import { CurrentUserQueryResponse } from 'core/graphql/queries/currentUser';

// Types
interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  requiresAdmin?: boolean;
  /** When set, a section subheader is shown before this item (first item in a group). */
  section?: string;
}

const DRAWER_WIDTH = 248;

// Styles
const useStyles = makeStyles((theme) => ({
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: '#031d35',
    color: '#fff',
    borderRight: '1px solid #2c4660',
  },
  drawerContainer: {
    overflow: 'auto',
  },
  content: {
    flexGrow: 1,
    padding: 0,
    marginTop: 64, // AppBar height
    backgroundColor: '#02172a',
    minHeight: 'calc(100vh - 64px)',
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: '#02172a',
    color: '#fff',
  },
  appBarTitle: {
    color: '#fff',
    fontWeight: 'bold',
  },
  menuButton: {
    marginRight: theme.spacing(2),
    color: '#fff',
  },
  drawerHeader: {
    paddingTop: theme.spacing(4.5),
    paddingBottom: theme.spacing(2.25),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: '#13324b',
  },
  drawerTitle: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '1.25rem',
  },
  divider: {
    backgroundColor: 'rgba(142, 165, 191, 0.38)',
  },
  listItem: {
    margin: theme.spacing(0.45, 0.9),
    paddingTop: theme.spacing(1.15),
    paddingBottom: theme.spacing(1.15),
    paddingLeft: theme.spacing(1.9),
    paddingRight: theme.spacing(1.5),
    borderRadius: 10,
    color: 'rgba(240, 247, 255, 0.84)',
    '&:hover': {
      backgroundColor: 'rgba(112, 171, 196, 0.18)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(112, 171, 196, 0.34)',
      '&:hover': {
        backgroundColor: 'rgba(112, 171, 196, 0.34)',
      },
    },
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      color: 'rgba(240, 247, 255, 0.84)',
      fontWeight: 500,
    },
  },
  listItemIcon: {
    minWidth: 36,
    color: 'rgba(224, 236, 248, 0.82)',
    '&.Mui-selected': {
      color: '#f7fbff',
    },
  },
  navSection: {
    color: '#9cb1c7',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: '36px',
  },
  userInfo: {
    color: '#fff',
  },
}));

// Navigation items
const navigationItems: NavigationItem[] = [
  {
    label: 'Dashboard',
    path: '/admin/dashboard',
    icon: <DashboardIcon />
  },
  {
    label: 'SuperAdmin',
    path: '/admin/superadmin',
    icon: <SuperAdminIcon />,
    requiresAdmin: true
  },
  {
    label: 'Affiliate Management',
    path: '/admin/affiliates',
    icon: <PeopleIcon />,
    requiresAdmin: true
  },
  {
    label: 'Payout Management',
    path: '/admin/payouts',
    icon: <PaymentIcon />,
    requiresAdmin: true
  },
  {
    label: 'Conversions',
    path: '/admin/conversions',
    icon: <TrendingUpIcon />,
    requiresAdmin: true
  },
  {
    label: 'Analytics',
    path: '/admin/analytics',
    icon: <AnalyticsIcon />,
    requiresAdmin: true
  },
  {
    label: 'Email templates',
    path: '/admin/emails',
    icon: <EmailIcon />,
    requiresAdmin: true,
    section: 'Communications',
  },
  {
    label: 'System Settings',
    path: '/admin/settings',
    icon: <SettingsIcon />,
    requiresAdmin: true
  }
];

const AdminLayout: FC<AdminLayoutProps> = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const tokenManager = TokenManagerStore;
  const history = useHistory();
  const location = useLocation();

  // Check if user is admin
  const { data: userData, loading: userLoading } = useQuery<CurrentUserQueryResponse>(CURRENT_USER);
  const currentUser = userData?.user || userData?.me || userData?.getCurrentUser;
  
  // Check multiple sources for admin status
  const storedIsAdmin = localStorage.getItem('isAdmin');
  const storedUser = localStorage.getItem('user');
  const storedUserData = (() => {
    if (!storedUser) return null;
    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  })();
  const storedAdminFlag = localStorage.getItem('admin');
  const devModeFlag = localStorage.getItem('dev-mode');
  const devUserType = localStorage.getItem('dev-user-type');
  
  // Also check token directly from TokenManager
  const token = tokenManager.token;
  let tokenRole: string | undefined;
  let tokenIsAdmin: boolean | undefined;
  if (token) {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      tokenRole = decoded.role;
      tokenIsAdmin = decoded.isAdmin === true;
    } catch {
      // Token decode failed, ignore
    }
  }
  
  // Check admin status from multiple sources (including token)
  const isAdmin = 
    storedIsAdmin === 'true' ||
    currentUser?.isAdmin === true ||
    currentUser?.role === 'admin' ||
    storedUserData?.isAdmin === true ||
    storedUserData?.role === 'admin' ||
    tokenRole === 'admin' ||
    tokenIsAdmin === true;

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const handleLogout = () => {
    tokenManager.clearToken();
    localStorage.removeItem("admin");
    window.location.href = `${window.location.origin}/login`;
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Filter navigation items based on admin permissions
  const filteredNavigationItems = navigationItems.filter(item => 
    !item.requiresAdmin || isAdmin
  );

  // Show loading state while checking permissions
  if (userLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading admin interface...</Typography>
      </Box>
    );
  }

  // Show access denied if not admin
  // In development, be more lenient - allow if on admin route
  const isLocalDevelopment = 
    process.env.NODE_ENV === 'development' || 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  
  // In local dev, if we're on an admin route, allow access (for testing)
  const isOnAdminRoute = location.pathname.startsWith('/admin/');
  const hasDevAccess = isLocalDevelopment && (
    devModeFlag === 'true' ||
    devUserType === 'admin' ||
    storedIsAdmin === 'true' ||
    storedAdminFlag === 'true' ||
    isOnAdminRoute // Fallback: if on admin route in dev, allow
  );
  
  // Additional check: if we're on admin.starfarm.com and have a valid token, allow access
  // The backend will handle the actual authorization
  const isOnAdminDomain = window.location.hostname === 'admin.starfarm.com' || 
                          window.location.hostname.includes('starfarm-admin');
  const hasValidToken = Boolean(token && token.length > 0);
  const allowBackendAuth = isOnAdminDomain && hasValidToken;
  const hasAccess = isAdmin || hasDevAccess || allowBackendAuth;
  
  if (!hasAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error" style={{ maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to access the admin interface. 
            Please contact your administrator if you believe this is an error.
          </Typography>
          {isLocalDevelopment && (
            <Typography variant="body2" style={{ marginTop: 16, color: '#999' }}>
              Dev hint: Set localStorage flags: isAdmin=true, admin=true, dev-mode=true
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  const drawer = (
    <div>
      <Box className={classes.drawerHeader} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <img src="/starFarm.png" alt="" style={{ width: 64, height: 64, borderRadius: '50%', marginBottom: 14 }} />
        <img
          src="https://starfarm-email-template-assets.s3.us-east-1.amazonaws.com/starfarm-wordmark.png"
          alt="starfarm"
          style={{ width: 170, height: 'auto' }}
        />
        <Typography style={{ color: '#8ea5bf', marginTop: 8, fontSize: '0.72rem', letterSpacing: '0.08em', fontWeight: 700 }}>
          ADMIN
        </Typography>
      </Box>
      <Divider className={classes.divider} />
      <List>
        {filteredNavigationItems.map((item, index) => {
          const prevSection = index > 0 ? filteredNavigationItems[index - 1].section : undefined;
          const showHeader = Boolean(item.section && item.section !== prevSection);
          return (
            <Fragment key={item.path}>
              {showHeader && (
                <ListSubheader className={classes.navSection} disableSticky>
                  {item.section!.toUpperCase()}
                </ListSubheader>
              )}
              <ListItem
                button
                selected={isActiveRoute(item.path)}
                className={classes.listItem}
                onClick={() => {
                  if (isMobile) setMobileOpen(false);
                  else setSidebarOpen(false);
                  history.push(item.path);
                }}
              >
                <ListItemIcon className={`${classes.listItemIcon} ${isActiveRoute(item.path) ? 'Mui-selected' : ''}`}>
                  {item.badge ? (
                    <Badge badgeContent={item.badge} color="secondary">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  className={classes.listItemText}
                  primaryTypographyProps={{
                    style: isActiveRoute(item.path)
                      ? { color: '#f7fbff', fontWeight: 700 }
                      : { color: 'rgba(240, 247, 255, 0.84)', fontWeight: 500 },
                  }}
                />
              </ListItem>
            </Fragment>
          );
        })}
      </List>
      <Divider className={classes.divider} />
      <List>
        <ListItem button onClick={handleLogout} className={classes.listItem}>
          <ListItemIcon className={classes.listItemIcon}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" className={classes.listItemText} />
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box 
      display="flex" 
      style={{ 
        margin: 0, 
        padding: 0, 
        minHeight: '100vh',
        backgroundColor: '#02172a'
      }}
    >
      <AppBar
        position="fixed"
        className={classes.appBar}
        style={{
          width: '100%',
          marginLeft: 0
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            style={{ marginRight: theme.spacing(2), color: '#fff' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap style={{ flexGrow: 1 }} className={classes.appBarTitle}>
            {filteredNavigationItems.find(item => isActiveRoute(item.path))?.label || 'Admin Dashboard'}
          </Typography>
          <Box display="flex" alignItems="center">
            <IconButton style={{ color: '#fff' }}>
              <Badge badgeContent={4} color="secondary">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Box display="flex" alignItems="center" ml={2}>
              <Avatar style={{ width: 32, height: 32, marginRight: 8, backgroundColor: '#4caf50' }}>
                {currentUser?.firstName?.charAt(0) || 'A'}
              </Avatar>
              <Typography variant="body2" className={classes.userInfo}>
                {currentUser?.firstName} {currentUser?.lastName}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          variant="temporary"
          anchor="left"
          open={isMobile ? mobileOpen : sidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
          classes={{
            paper: classes.drawerPaper
          }}
        >
          {drawer}
        </Drawer>
      </nav>

      <Box
        component="main"
        className={classes.content}
        style={{
          width: '100vw',
          marginLeft: 0,
          marginRight: 0,
          paddingLeft: 0,
          paddingRight: 0,
          border: 'none',
          outline: 'none',
          backgroundColor: '#02172a'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default observer(AdminLayout);
